/**
 * Shape of `GET /health/status` (web/health.py: HealthReader.status_payload)
 * and the rules that turn it into operator-facing severity.
 *
 * The interpretation rules live here rather than in the page so they are unit
 * testable — the interesting part of this screen is not the layout, it is
 * knowing which numbers mean the energy checkpoint writer is actually behind.
 *
 * Whether an optional component is expected to run at all is the backend's
 * verdict, carried as `enabled` (read from the deployment's config). These rules
 * never re-derive it; `enabled: false` is shown as "off" and not evaluated.
 *
 * Likewise whether a failure is the component's own or an unreachable store it
 * reports into is the backend's verdict, carried as `blocked_by`. A blocked
 * component is "degraded" (not verifiable); the alarm belongs to the dependency's
 * own entry. These rules never infer it from `reason` text.
 *
 * Issue and note sentences come from the catalog: callers rendering them pass
 * their `t`; without one the runtime's active catalog is used at call time.
 * Backend `reason`/`last_error` text is shown as sent.
 */

import { formatNumber } from "@voltie/localization-web";
import { localization, type Translate } from "@/app/localization";

/* ------------------------------------------------------------------ */
/*  Payload types                                                      */
/* ------------------------------------------------------------------ */

export interface CoreHealth {
  ok: boolean;
  pid?: number;
  uptime_s?: number;
  reason?: string | null;
}

/** Redis / Postgres: a live probe with a round-trip time. */
export interface ProbeHealth {
  ok: boolean;
  latency_ms?: number;
  reason?: string | null;
}

/**
 * The live Redis (telemetry / diagnostics projections). `shared` = no separate
 * server, everything lives on the control Redis.
 */
export interface LiveRedisHealth extends ProbeHealth {
  mode?: "shared" | "split";
}

/**
 * The dedicated Redis of the diagnostics logs mirror (DIAG_LOG_REDIS_URL).
 * Present only when the deployment configures one; absent = the logs mirror
 * reports into the live Redis. `config_error` is a fixed code of a rejected
 * configuration, never the configured value.
 */
export interface DiagLogsRedisHealth extends ProbeHealth {
  mode?: "dedicated";
  config_error?: string | null;
}

/** The `/health/status` entries a component's state can be blocked by. */
export type HealthDependency = "redis" | "redis_live" | "redis_diag_logs";

/**
 * Backend verdict on whether the deployment's config runs an optional component
 * (`enabled`), and — when its state could not be read because the Redis it
 * reports into is unreachable — which dependency blocked the check
 * (`blocked_by`). Both are absent on payloads from a core that predates them.
 */
export interface OptionalComponent {
  enabled?: boolean;
  blocked_by?: HealthDependency | null;
}

/** A component that publishes a heartbeat key into Redis. */
export interface HeartbeatHealth extends OptionalComponent {
  ok: boolean;
  last_heartbeat_age_s?: number | null;
  pid?: number | null;
  hostname?: string | null;
  reason?: string | null;
}

export interface UdpListenerHealth extends HeartbeatHealth {
  /** Enabled sub-components whose own heartbeat is missing or stale. */
  missing_components?: string[];
  /** Sub-components that could not be checked, and the dependency that blocked them. */
  blocked_components?: Record<string, HealthDependency>;
  /** Sub-components that report but are losing work (the logs mirror's writes fail). */
  degraded_components?: string[];
}

export interface DiagnosticsLogsHealth extends HeartbeatHealth {
  /** Age of the last successful write; for older writers, of the last heartbeat. */
  last_push_age_s?: number | null;
  /** Alive, but its latest write failed. null when the writer does not report it. */
  write_error_active?: boolean | null;
  /** True when the heartbeat is fresh and writes are failing. */
  degraded?: boolean;
  write_failures?: number | null;
  last_write_ok_age_s?: number | null;
  pending?: number | null;
  entries_written?: number | null;
  dropped_entries?: number | null;
}

export interface DiagnosticsMetricsHealth extends HeartbeatHealth {
  snapshot_age_s?: number | null;
  ttl_remaining_s?: number | null;
}

export interface EnergyWriterHealth extends OptionalComponent {
  ok: boolean;
  running?: boolean;
  reason?: string | null;
  redis_version?: string | null;
  redis_supports_zadd_lt?: boolean | null;
  redis_version_checked?: boolean;
  heartbeat_age_s?: number | null;
  dirty_backlog?: number | null;
  claimed_in_flight?: number | null;
  oldest_dirty_age_ms?: number | null;
  tick_duration_ms?: number | null;
  tick_ms_p99?: number | null;
  tick_overran_interval?: boolean;
  ticks_overran?: number | null;
  rows_per_second?: number | null;
  capacity_rows_per_second?: number | null;
  capacity_rows_per_tick?: number | null;
  target_freshness_seconds?: number | null;
  projected_drain_seconds?: number | null;
  reference_fleet_units?: number | null;
  interval_seconds?: number | null;
  rows_written?: number | null;
  rows_rejected?: number | null;
  remarked_advanced?: number | null;
  released_on_failure?: number | null;
  missing_snapshots?: number | null;
  corrupt_snapshots?: number | null;
  reclaimed_expired_leases?: number | null;
  batch_ms_p50?: number | null;
  batch_ms_p99?: number | null;
  batch_ms_max?: number | null;
  ticks?: number | null;
  ticks_failed?: number | null;
}

/**
 * Hydration counters come straight out of HGETALL, so they arrive as strings.
 * `rows_failed` is not published by every writer build — absent means "this
 * deployment does not report it", never zero.
 */
export interface EnergyHydrationHealth extends OptionalComponent {
  ok: boolean;
  completed?: boolean;
  completed_at?: string | null;
  started_at?: string | null;
  rows_scanned?: string | number | null;
  rows_hydrated?: string | number | null;
  rows_repaired?: string | number | null;
  rows_skipped_lower?: string | number | null;
  rows_failed?: string | number | null;
  redis_round_trips?: string | number | null;
  last_error?: string | null;
  reason?: string | null;
}

export interface HealthStatus {
  checked_at: string;
  core: CoreHealth;
  redis: ProbeHealth;
  /** Absent on payloads from a core that predates the control/live split. */
  redis_live?: LiveRedisHealth;
  /** Only when the logs mirror has a dedicated Redis. */
  redis_diag_logs?: DiagLogsRedisHealth;
  postgres: ProbeHealth;
  udp_listener: UdpListenerHealth;
  cmd_channel_listener: HeartbeatHealth;
  diagnostics_logs: DiagnosticsLogsHealth;
  diagnostics_metrics: DiagnosticsMetricsHealth;
  energy_writer: EnergyWriterHealth;
  energy_hydration: EnergyHydrationHealth;
}

/* ------------------------------------------------------------------ */
/*  Severity                                                           */
/* ------------------------------------------------------------------ */

export type Severity = "ok" | "warning" | "critical" | "off";

export interface HealthIssue {
  level: "WARNING" | "CRITICAL";
  message: string;
}

/** Thresholds, named so the page can print what it is comparing against. */
export const HEARTBEAT_CRITICAL_AGE_S = 90;
export const OLDEST_DIRTY_WARN_MS = 120_000;
export const OLDEST_DIRTY_CRITICAL_MS = 300_000;
/** Throughput below this fraction of configured capacity is starved. */
export const THROUGHPUT_STARVED_RATIO = 0.5;
/** A backlog is only "large" relative to capacity: more than a minute of work. */
export const LARGE_BACKLOG_SECONDS_OF_CAPACITY = 60;

const RANK: Record<Severity, number> = { off: 0, ok: 1, warning: 2, critical: 3 };

export function worstSeverity(levels: Severity[]): Severity {
  return levels.reduce<Severity>((acc, s) => (RANK[s] > RANK[acc] ? s : acc), "ok");
}

export function severityOfIssues(issues: HealthIssue[]): Severity {
  if (issues.some((i) => i.level === "CRITICAL")) return "critical";
  if (issues.length > 0) return "warning";
  return "ok";
}

/** Redis HGETALL values arrive as strings; anything unparseable is "unknown". */
export function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Operator text for a component the backend could not check; null when it could. */
export function blockedNote(
  c: { blocked_by?: HealthDependency | null } | undefined,
  t: Translate = localization.t,
): string | null {
  if (!c?.blocked_by) return null;
  return t("operator.ops.health.blocked_note", { dependency: c.blocked_by, raw: String(c.blocked_by) });
}

/* ------------------------------------------------------------------ */
/*  Energy writer                                                      */
/* ------------------------------------------------------------------ */

/**
 * Is a checkpoint writer expected to be running in this deployment?
 *
 * The backend says so in `enabled`, from ENERGY_METER_MODE /
 * ENERGY_PG_CHECKPOINT_ENABLED. Only a payload from a core that predates that
 * field falls back to the old inference from the hydration marker — the
 * conservative reading, since a marker means the pipeline once ran here.
 */
export function energyWriterExpected(
  w: EnergyWriterHealth | undefined,
  hydration: EnergyHydrationHealth | undefined,
): boolean {
  if (typeof w?.enabled === "boolean") return w.enabled;
  if (!hydration) return false;
  return Boolean(hydration.completed || hydration.started_at || hydration.completed_at);
}

/**
 * Why an absent writer heartbeat is being read the way it is: the config verdict,
 * or — on a legacy payload only — the hydration-marker inference, named as such
 * so a wrong guess is visible rather than silently trusted. Null when the writer
 * is reporting a heartbeat.
 */
export function writerAbsenceNote(
  w: EnergyWriterHealth | undefined,
  hydration?: EnergyHydrationHealth,
  t: Translate = localization.t,
): string | null {
  if (!w || w.running !== false) return null;
  const basis = w.enabled === false
    ? "config_disabled"
    : w.enabled === true
      ? "config_enabled"
      : energyWriterExpected(w, hydration) ? "marker_present" : "marker_absent";
  return t("operator.ops.health.writer_absence", { basis });
}

export function evaluateEnergyWriter(
  w: EnergyWriterHealth | undefined,
  hydration?: EnergyHydrationHealth,
  t: Translate = localization.t,
): HealthIssue[] {
  if (!w || w.enabled === false) return [];
  const blocked = blockedNote(w, t);
  if (blocked) return [{ level: "WARNING", message: blocked }];

  if (w.running === false) {
    // A stopped writer's leftover counters are not evaluated: one issue, or none
    // when it is not expected to run (reported as "off", not as a fault).
    if (!energyWriterExpected(w, hydration)) return [];
    return [{
      level: "CRITICAL",
      message: t("operator.ops.health.writer_no_heartbeat", { basis: w.enabled === true ? "config" : "hydration" }),
    }];
  }

  const issues: HealthIssue[] = [];
  if (w.ok === false) {
    issues.push({
      level: "CRITICAL",
      message: w.reason || t("operator.ops.health.writer_not_ok"),
    });
  }

  if (w.redis_supports_zadd_lt === false) {
    issues.push({
      level: "CRITICAL",
      message: t("operator.ops.health.redis_no_zadd_lt", {
        version: w.redis_version || t("operator.ops.health.redis_version_unknown"),
      }),
    });
  }

  const beat = num(w.heartbeat_age_s);
  if (beat !== null && beat > HEARTBEAT_CRITICAL_AGE_S) {
    issues.push({
      level: "CRITICAL",
      message: t("operator.ops.health.writer_heartbeat_old", { age: Math.round(beat), limit: HEARTBEAT_CRITICAL_AGE_S }),
    });
  }

  // Lag, not size. The dirty ZSET score is first_dirty_at and is never refreshed
  // while a unit waits, so this is the age of the longest-waiting unit — exactly
  // the window a Redis loss would destroy. A big backlog on its own is normal
  // for a 100k fleet and is deliberately NOT an issue here.
  const oldest = num(w.oldest_dirty_age_ms);
  if (oldest !== null) {
    if (oldest > OLDEST_DIRTY_CRITICAL_MS) {
      issues.push({
        level: "CRITICAL",
        message: t("operator.ops.health.oldest_dirty_critical", { waited: Math.round(oldest / 1000), limit: OLDEST_DIRTY_CRITICAL_MS / 1000 }),
      });
    } else if (oldest > OLDEST_DIRTY_WARN_MS) {
      issues.push({
        level: "WARNING",
        message: t("operator.ops.health.oldest_dirty_warning", { waited: Math.round(oldest / 1000), limit: OLDEST_DIRTY_WARN_MS / 1000 }),
      });
    }
  }

  const overran = num(w.ticks_overran);
  if (w.tick_overran_interval === true || (overran !== null && overran > 0)) {
    issues.push({
      level: "WARNING",
      message: w.tick_overran_interval
        ? t("operator.ops.health.tick_overran_last")
        : t("operator.ops.health.ticks_overran", { count: overran ?? 0 }),
    });
  }

  // Backlog only matters together with throughput: a large queue that capacity
  // is not being delivered against is a queue that will not drain.
  const backlog = num(w.dirty_backlog);
  const rps = num(w.rows_per_second);
  const capacity = num(w.capacity_rows_per_second);
  if (backlog !== null && rps !== null && capacity !== null && capacity > 0) {
    const largeBacklog = backlog > capacity * LARGE_BACKLOG_SECONDS_OF_CAPACITY;
    if (largeBacklog && rps < capacity * THROUGHPUT_STARVED_RATIO) {
      issues.push({
        level: "WARNING",
        message: t("operator.ops.health.backlog_starved", {
          backlog: formatNumber(backlog, 0),
          rate: formatNumber(rps, 1),
          capacity: formatNumber(capacity, 1),
        }),
      });
    }
  }

  return issues;
}

/* ------------------------------------------------------------------ */
/*  Energy hydration                                                   */
/* ------------------------------------------------------------------ */

/**
 * Hydration repairs Redis from PostgreSQL after a Redis loss. It is load-bearing
 * for the rollout gate: if the checkpoint writer is live and hydration has not
 * completed, baselines may have been seeded from zero.
 */
export function evaluateEnergyHydration(
  h: EnergyHydrationHealth | undefined,
  writer?: EnergyWriterHealth,
  t: Translate = localization.t,
): HealthIssue[] {
  // Switched off by config: the marker is history, its counters are not alarms.
  if (!h || h.enabled === false) return [];
  const blocked = blockedNote(h, t);
  if (blocked) return [{ level: "WARNING", message: blocked }];
  const issues: HealthIssue[] = [];

  if (h.ok === false) {
    issues.push({ level: "CRITICAL", message: h.reason || t("operator.ops.health.hydration_unreadable") });
  }

  const meterActive = writer?.running === true;
  if (meterActive && h.completed !== true) {
    issues.push({
      level: "CRITICAL",
      message: t("operator.ops.health.not_hydrated", { detail: h.reason ? "reason" : "none", reason: h.reason ?? "" }),
    });
  }

  const failed = num(h.rows_failed);
  if (failed !== null && failed > 0) {
    issues.push({ level: "CRITICAL", message: t("operator.ops.health.rows_failed", { count: failed }) });
  }

  if (h.last_error) {
    issues.push({ level: "CRITICAL", message: t("operator.ops.health.last_hydration_error", { error: h.last_error }) });
  }

  return issues;
}

/* ------------------------------------------------------------------ */
/*  Generic components + overall roll-up                               */
/* ------------------------------------------------------------------ */

/**
 * Severity of a plain ok/reason component. A component the backend could not
 * check because a dependency is down is "degraded" — the dependency's own entry
 * carries that outage's severity. The UDP listener additionally reports
 * sub-components: alive but incomplete (missing or not verifiable) is a warning,
 * not a green light.
 */
export function componentSeverity(
  c:
    | {
        ok?: boolean;
        enabled?: boolean;
        blocked_by?: HealthDependency | null;
        missing_components?: string[];
        blocked_components?: Record<string, HealthDependency>;
        degraded?: boolean;
        degraded_components?: string[];
      }
    | undefined,
): Severity {
  if (!c) return "warning";
  if (c.enabled === false) return "off";
  if (c.blocked_by) return "warning";
  if (c.ok === false && c.degraded === true) return "warning";
  if (c.ok === false) return "critical";
  if (c.missing_components && c.missing_components.length > 0) return "warning";
  if (c.blocked_components && Object.keys(c.blocked_components).length > 0) return "warning";
  if (c.degraded_components && c.degraded_components.length > 0) return "warning";
  return "ok";
}

/**
 * The live Redis is not a readiness dependency — nothing on the charging path
 * reads it — but telemetry and diagnostics projections live there. Down is
 * "degraded", never "critical"; unreported (a core older than the split) is
 * "degraded" too, so an unknown is not shown as healthy.
 */
export function liveRedisSeverity(r: LiveRedisHealth | undefined): Severity {
  if (!r || r.ok === false) return "warning";
  return "ok";
}

/**
 * Only the Logs page depends on the dedicated diagnostics logs Redis, so down
 * (or a rejected configuration) is "degraded", never "critical". Absent means
 * the deployment has none: nothing to rate.
 */
export function diagLogsRedisSeverity(r: DiagLogsRedisHealth | undefined): Severity {
  if (!r) return "off";
  return r.ok === false ? "warning" : "ok";
}

export function energyWriterSeverity(
  w: EnergyWriterHealth | undefined,
  hydration?: EnergyHydrationHealth,
): Severity {
  if (!w) return "warning";
  if (w.enabled === false) return "off";
  const issues = evaluateEnergyWriter(w, hydration);
  if (issues.length === 0 && w.running === false) return "off";
  return severityOfIssues(issues);
}

export function energyHydrationSeverity(
  h: EnergyHydrationHealth | undefined,
  writer?: EnergyWriterHealth,
): Severity {
  if (h?.enabled === false) return "off";
  return severityOfIssues(evaluateEnergyHydration(h, writer));
}

export function overallSeverity(s: HealthStatus | undefined): Severity {
  if (!s) return "warning";
  return worstSeverity([
    componentSeverity(s.core),
    componentSeverity(s.redis),
    liveRedisSeverity(s.redis_live),
    diagLogsRedisSeverity(s.redis_diag_logs),
    componentSeverity(s.postgres),
    componentSeverity(s.udp_listener),
    componentSeverity(s.cmd_channel_listener),
    componentSeverity(s.diagnostics_logs),
    componentSeverity(s.diagnostics_metrics),
    energyWriterSeverity(s.energy_writer, s.energy_hydration),
    energyHydrationSeverity(s.energy_hydration, s.energy_writer),
  ]);
}
